import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertIcon, Button, ButtonGroup, Card, CardBody, Flex, Heading, Text } from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { Loader } from '@netiva/classifieds-ui';

import { useAppSelector } from '@/store';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks';

export const InvitationsPage: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const { invitations, hasValidAccount } = useAppSelector((s) => s.account);

  const token = searchParams.get('token') ?? undefined;
  const isMissingToken = !hasValidAccount && !token;

  const { isLoading: isLoadingList } = accountApi.useGetAccountInvitations(
    hasValidAccount ? {} : token ? { token } : {}
  );

  const [acceptInvitation, { isLoading: isAccepting }] = accountApi.useAcceptAccountInvitation();
  const [rejectInvitation, { isLoading: isRejecting }] = accountApi.useRejectAccountInvitation();

  const isLoading = isLoadingList || isAccepting || isRejecting;

  const handleAccept = async (accountId: number) => {
    await acceptInvitation({ accountId });
  };

  const handleReject = async (accountId: number) => {
    await rejectInvitation({ accountId });
  };

  return (
    <>
      <Loader isLoading={isLoading} showOverlay />

      <Flex direction="column" gap={6}>
        <Heading size="md">{t('invitations.list.title', { count: invitations.length })}</Heading>

        {isMissingToken && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {t('invitations.error.invalidLink')}
          </Alert>
        )}

        {invitations.length ? (
          <>
            <Flex direction="column" gap={4}>
              {invitations.map((invitation) => (
                <Card key={invitation.accountId} background="surface">
                  <CardBody padding={4}>
                    <Flex align="center" gap={4}>
                      <Text fontWeight="bold">{invitation.accountName}</Text>
                      {hasValidAccount ? (
                        <ButtonGroup marginLeft="auto">
                          <Button colorScheme="neutral" onClick={() => handleReject(invitation.accountId)}>
                            {t('invitations.reject')}
                          </Button>
                          <Button onClick={() => handleAccept(invitation.accountId)}>{t('invitations.accept')}</Button>
                        </ButtonGroup>
                      ) : (
                        <Flex marginLeft="auto" />
                      )}
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </Flex>

            {!hasValidAccount && (
              <>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  {t('invitations.loginMessage')}
                </Alert>
                <div>
                  <Button type="button" size="sm" display="inline-flex" onClick={login}>
                    {t('ui.buttons.signin')}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {!isMissingToken && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                {t('invitations.list.empty')}
              </Alert>
            )}{' '}
          </>
        )}
      </Flex>
    </>
  );
};
